import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCrearColadaManual } from '@/hooks/useColadas';
import { useActividades } from '@/hooks/useLaboratorio';
import { usePlantas } from '@/hooks/usePlantas';
import { normalizarEntradaDecimal } from '@/lib/utils';
import { Loader2, Truck } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * `/coladas/registrar-fiori` — registro manual de una colada de las
 * autocargables Fiori (no tienen dosificador ni CSV, así que no pasan por
 * la ingesta/agrupador automático).
 *
 * Pensada para usarse desde el celular en la obra: una sola columna,
 * campos mínimos, botón grande. El "Tipo de Concreto" es texto libre a
 * propósito (ej. "Colado Muros") — se homologa a un producto BC después
 * desde `/admin/mapeo-recetas`, igual que las recetas Blend.
 */
export function RegistrarColadaFioriPantalla() {
  const navegar = useNavigate();
  const plantasQ = usePlantas();
  const actividadesQ = useActividades();
  const crear = useCrearColadaManual();

  const plantasFiori = (plantasQ.data?.data?.plantas ?? []).filter((p) => p.marca === 'Fiori');
  const actividades = actividadesQ.data?.data?.actividades ?? [];

  const errorPlantas = plantasQ.data?.error?.mensaje ?? (plantasQ.error?.message || null);
  const errorActividades =
    actividadesQ.data?.error?.mensaje ?? (actividadesQ.error?.message || null);

  const [idPlanta, setIdPlanta] = useState('');
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [destino, setDestino] = useState('');
  const [recetaTexto, setRecetaTexto] = useState('');
  const [m3, setM3] = useState('');
  const [idActividad, setIdActividad] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const m3Numero = Number(m3);
  const formValido =
    idPlanta !== '' &&
    fecha.trim() !== '' &&
    destino.trim() !== '' &&
    recetaTexto.trim() !== '' &&
    Number.isFinite(m3Numero) &&
    m3Numero > 0;

  const enviar = () => {
    if (!formValido) return;
    crear.mutate(
      {
        id_planta: Number(idPlanta),
        fecha,
        destino_raw: destino.trim(),
        receta_texto: recetaTexto.trim(),
        m3_producidos: m3Numero,
        id_actividad: idActividad === '' ? null : Number(idActividad),
        observaciones: observaciones.trim() === '' ? null : observaciones.trim(),
      },
      {
        onSuccess: (r) => {
          if (r.data) navegar(`/coladas/${r.data.colada.id_colada}`);
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-xl">
            <Truck className="h-6 w-6" /> Registrar colada Fiori
          </CardTitle>
          <p className="text-base text-muted-foreground">
            Para las autocargables, que no reportan por dosificador. Completá los datos del colado.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {errorPlantas && (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive">
              No se pudo cargar la lista de máquinas: {errorPlantas}
            </p>
          )}
          {!errorPlantas && !plantasQ.isLoading && plantasFiori.length === 0 && (
            <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800">
              No hay ninguna planta marca "Fiori" activa en el sistema. Avisá a Admin para revisar
              /admin/... (hor.plantas).
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="maquina" className="text-base">
              Máquina
            </Label>
            <Select value={idPlanta} onValueChange={setIdPlanta} disabled={plantasQ.isLoading}>
              <SelectTrigger id="maquina" className="h-14 text-base">
                <SelectValue
                  placeholder={plantasQ.isLoading ? 'Cargando máquinas...' : 'Elegir máquina...'}
                />
              </SelectTrigger>
              <SelectContent>
                {plantasFiori.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)} className="text-base">
                    {p.codigo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fecha" className="text-base">
              Fecha
            </Label>
            <Input
              id="fecha"
              type="date"
              // `lang` fuerza el formato dd/mm/aaaa del selector nativo —
              // sin esto, el navegador usa el locale del sistema operativo
              // (en Windows en inglés eso sale mm/dd/aaaa, confuso para el
              // operador de campo).
              lang="es-CR"
              className="h-14 text-base"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="destino" className="text-base">
              Obra o Centro de Costo
            </Label>
            <Input
              id="destino"
              className="h-14 text-base"
              placeholder="Ej: Casa D18"
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="receta" className="text-base">
              Tipo de Concreto
            </Label>
            <Input
              id="receta"
              className="h-14 text-base"
              placeholder="Ej: Colado Muros"
              value={recetaTexto}
              onChange={(e) => setRecetaTexto(e.target.value)}
              maxLength={50}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="m3" className="text-base">
              m³ producidos
            </Label>
            <Input
              id="m3"
              type="text"
              inputMode="decimal"
              className="h-14 text-base"
              value={m3}
              onChange={(e) => setM3(normalizarEntradaDecimal(e.target.value))}
            />
          </div>

          {errorActividades && (
            <p className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive">
              No se pudo cargar el catálogo de actividades: {errorActividades}
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="actividad" className="text-base">
              Actividad
            </Label>
            <Select value={idActividad} onValueChange={setIdActividad}>
              <SelectTrigger id="actividad" className="h-14 text-base">
                <SelectValue placeholder="Elegir actividad (opcional)..." />
              </SelectTrigger>
              <SelectContent>
                {actividades.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)} className="text-base">
                    {a.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion" className="text-base">
              Descripción
            </Label>
            <Textarea
              id="descripcion"
              placeholder="Notas adicionales (opcional)"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={4}
              className="text-base"
            />
          </div>

          <Button
            size="lg"
            className="h-16 w-full gap-2 text-lg"
            disabled={!formValido || crear.isPending}
            onClick={enviar}
          >
            {crear.isPending && <Loader2 className="h-5 w-5 animate-spin" />}
            Registrar colada
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
